/**
 * tutorPrepareLiveAdapter — « Préparer mon live » (`tutor/prepare-live`) : les lives du jour et du
 * lendemain du masteur (`GET tutors/{id}/today-live-occurrence`, `LiveOccurrenceParticipantResource`
 * pour chaque élève), les programmes de révision d'un élève (`getStudyProgramsAsTutor`), et la règle
 * des événements `live_preparation.*`.
 *
 * Ports littéraux de `tutor/lives/PrepareLiveScreen.js` (`getSessionStats`,
 * `checkPreparationCompletion`, `getSelectedSubjectsDisplay`) et du sélecteur des chapitres
 * (`common/chapters/StudyProgramsFilterList.js`, `StudyProgramChapterSelector.js`,
 * `studyPrograms/components/DaySectionComponent.js`). Jamais importé par le DS.
 */
import moment from "moment"
import "moment/locale/fr"

import { parseSubjectIds } from "../../../../../data/utility/Utils"
import { type UISubject } from "../../../ui/types"
import { type UITutorPrepareOccurrence, type UITutorPrepareStudent } from "../../../ui/types/tutorLives"
import { type UITutorProgramChapter, type UITutorStudyProgram } from "../../../ui/types/tutorEleves"
import { indexSubjectsById, toUISubject, type ApiSchoolSubject } from "../../parent/shared/schoolSubjectAdapter"

// ── Formes du serveur ───────────────────────────────────────────────────────

/** Un élève d'une occurrence (`LiveOccurrenceParticipantResource`). */
export type ApiPrepareStudent = {
    id?: string | number
    firstname?: string | null
    lastname?: string | null
    avatar?: string | null
    class?: string | null
    chapter_id?: string | number | null
    chapter?: { id?: string | number; name?: string | null; subject?: ApiSchoolSubject | null } | null
    subject_ids?: Array<string | number> | string | null
    connection_duration?: number | null
    has_study_program?: boolean | null
    has_checkpoint?: boolean | null
    pedagogical_objective?: { subject_id?: string | number | null } | null
}

export type ApiPrepareOccurrence = {
    id?: string | number
    date?: string | null
    students?: ApiPrepareStudent[] | null
}

type ApiProgramChapter = {
    id?: string | number
    name?: string | null
    school_subject_id?: string | number | null
    parent_chapter?: { name?: string | null } | null
}

export type ApiTutorStudyProgram = {
    id?: string | number
    title?: string | null
    class?: string | null
    start_date?: string | null
    school_subject?: ApiSchoolSubject | null
    school_subjects?: ApiSchoolSubject[] | null
    study_program_items?: Array<{
        id?: string | number
        day?: number | null
        chapter?: ApiProgramChapter | null
    }> | null
}

// ── Les lives à préparer ────────────────────────────────────────────────────

/** Ce que la ligne d'un élève garde pour ses gestes (pas rendu). */
export type TutorPrepareStudentRow = UITutorPrepareStudent & {
    /** L'occurrence de l'élève : `{ id }` des deux écritures. */
    occurrenceId: string | number
}

export type TutorPrepareOccurrenceRow = UITutorPrepareOccurrence & { students: TutorPrepareStudentRow[] }

/** La clé d'une mise à jour en vol, celle de l'ancien `updatingKey`. */
export function prepareUpdateKey(studentId: string | number, occurrenceId: string | number): string {
    return `${studentId}-${occurrenceId}`
}

function toStudent(
    raw: ApiPrepareStudent,
    occurrenceId: string | number,
    subjectsById: Record<string, ApiSchoolSubject>,
    updatingKey: string | null
): TutorPrepareStudentRow | null {
    if (raw.id == null) return null
    const chapterId = raw.chapter?.id ?? raw.chapter_id ?? null
    const chapterSubject = raw.chapter?.subject
    const subject = chapterSubject != null ? (subjectsById[String(chapterSubject.id)] ?? chapterSubject) : undefined
    const subjectId = parseSubjectIds(raw.subject_ids)[0] as string | number | undefined
    const usualSubjectId = raw.pedagogical_objective?.subject_id ?? undefined
    return {
        id: raw.id,
        firstname: raw.firstname ?? "",
        ...(raw.lastname ? { lastname: raw.lastname } : {}),
        ...(raw.avatar ? { avatarUri: raw.avatar } : {}),
        ...(raw.class ? { className: raw.class } : {}),
        hasStudyProgram: !!raw.has_study_program,
        ...(raw.has_checkpoint ? { hasCheckpoint: true } : {}),
        ...((raw.connection_duration ?? 0) > 0 ? { connected: true } : {}),
        ...(chapterId != null
            ? {
                  chapter: {
                      id: chapterId,
                      name: raw.chapter?.name ?? "",
                      ...(subject != null ? { subject: toUISubject(subject) } : {}),
                  },
              }
            : {}),
        ...(subjectId != null ? { subjectId } : {}),
        ...(usualSubjectId != null ? { usualSubjectId } : {}),
        ...(updatingKey === prepareUpdateKey(raw.id, occurrenceId) ? { updating: true } : {}),
        occurrenceId,
    }
}

export function toTutorPrepareOccurrences(
    raws: ReadonlyArray<ApiPrepareOccurrence | null | undefined> | null | undefined,
    schoolSubjects: ReadonlyArray<ApiSchoolSubject | null | undefined> | null | undefined,
    updatingKey: string | null = null
): TutorPrepareOccurrenceRow[] {
    const subjectsById = indexSubjectsById(schoolSubjects as ApiSchoolSubject[] | null | undefined)
    return (raws ?? [])
        .map((raw): TutorPrepareOccurrenceRow | null => {
            if (raw?.id == null || !raw.date) return null
            const occurrenceId = raw.id
            return {
                id: occurrenceId,
                date: moment(raw.date).format("YYYY-MM-DDTHH:mm:ss"),
                students: (raw.students ?? [])
                    .map((student) =>
                        student == null ? null : toStudent(student, occurrenceId, subjectsById, updatingKey)
                    )
                    .filter((student): student is TutorPrepareStudentRow => student != null),
            }
        })
        .filter((occurrence): occurrence is TutorPrepareOccurrenceRow => occurrence != null)
}

/** Les matières qu'on choisit pour un élève hors programme : celles de `static-data`. */
export function toPrepareSubjects(
    schoolSubjects: ReadonlyArray<ApiSchoolSubject | null | undefined> | null | undefined
): UISubject[] {
    return (schoolSubjects ?? [])
        .filter((subject): subject is ApiSchoolSubject => subject?.id != null)
        .map((subject) => toUISubject(subject))
}

// ── La mesure de la préparation (`checkPreparationCompletion`) ──────────────

/** Un élève compte s'il n'est pas pris par un checkpoint ; il est prêt avec son chapitre, ou sa matière. */
function stats(occurrences: UITutorPrepareOccurrence[]): { done: number; total: number } {
    const counted = occurrences.flatMap((occurrence) => occurrence.students).filter((student) => !student.hasCheckpoint)
    const done = counted.filter((student) =>
        student.hasStudyProgram ? student.chapter != null : student.subjectId != null
    )
    return { done: done.length, total: counted.length }
}

const allPrepared = (occurrences: UITutorPrepareOccurrence[]) => {
    const { done, total } = stats(occurrences)
    return total > 0 && done === total
}

/**
 * Les événements de complétude que la liste mérite maintenant, et que l'écran n'a pas encore envoyés
 * (`tracked`) : un live entièrement préparé (une fois par live), tous ceux du jour, tous ceux du jour
 * et du lendemain (une fois chacun).
 */
export function preparationCompletionEvents(
    occurrences: UITutorPrepareOccurrence[],
    tracked: ReadonlySet<string>,
    now: moment.Moment | Date | number = moment()
): string[] {
    const events: string[] = []
    for (const occurrence of occurrences) {
        const key = `live:${occurrence.id}`
        if (!tracked.has(key) && allPrepared([occurrence])) events.push(key)
    }
    const today = occurrences.filter((occurrence) => moment(occurrence.date).isSame(moment(now), "day"))
    if (!tracked.has("today") && allPrepared(today)) events.push("today")
    if (!tracked.has("all") && allPrepared(occurrences)) events.push("all")
    return events
}

/** Le nom Amplitude d'une clé de `preparationCompletionEvents` (les noms de l'ancien écran). */
export function preparationEventName(key: string): string {
    if (key === "today") return "live_preparation.today_lives_prepared"
    if (key === "all") return "live_preparation.today_and_tomorrow_lives_prepared"
    return "live_preparation.live_session_prepared"
}

// ── Les programmes de révision d'un élève ───────────────────────────────────

/**
 * Un programme, jour par jour — la boîte « Sélectionner un chapitre ». Le libellé est celui de la
 * pilule de l'ancien filtre (« Mathématiques - 4ème - Révisions de Toussaint ») ; un jour se titre
 * « Jour 1 · lundi 26 octobre » quand le programme a une date de départ. La progression de l'élève sur
 * chaque chapitre n'est pas servie par cette lecture : pas de barre.
 */
export function toUITutorStudyPrograms(
    raws: ReadonlyArray<ApiTutorStudyProgram | null | undefined> | null | undefined,
    schoolSubjects: ReadonlyArray<ApiSchoolSubject | null | undefined> | null | undefined
): UITutorStudyProgram[] {
    const staticById = indexSubjectsById(schoolSubjects as ApiSchoolSubject[] | null | undefined)
    return (raws ?? [])
        .map((raw): UITutorStudyProgram | null => {
            if (raw?.id == null) return null
            const subjectsById = { ...indexSubjectsById(raw.school_subjects), ...staticById }
            const programSubject = raw.school_subject ?? raw.school_subjects?.[0] ?? null
            const days = new Map<number, UITutorStudyProgram["days"][number]>()
            for (const item of raw.study_program_items ?? []) {
                const chapter = item?.chapter
                if (chapter?.id == null || !chapter.name || item?.day == null) continue
                let day = days.get(item.day)
                if (day == null) {
                    const date = raw.start_date ? moment(raw.start_date).add(item.day - 1, "days") : null
                    day = {
                        id: `${raw.id}-${item.day}`,
                        title: `Jour ${item.day}${date != null ? ` · ${date.locale("fr").format("dddd D MMMM")}` : ""}`,
                        chapters: [],
                    }
                    days.set(item.day, day)
                }
                const subject =
                    chapter.school_subject_id != null ? subjectsById[String(chapter.school_subject_id)] : null
                const programChapter: UITutorProgramChapter = {
                    id: chapter.id,
                    name: chapter.name,
                    ...(chapter.parent_chapter?.name ? { parentName: chapter.parent_chapter.name } : {}),
                    subject: toUISubject(subject ?? programSubject),
                }
                day.chapters.push(programChapter)
            }
            return {
                id: raw.id,
                label: [programSubject?.name, raw.class, raw.title].filter(Boolean).join(" - "),
                subjects:
                    programSubject != null
                        ? [toUISubject(staticById[String(programSubject.id)] ?? programSubject)]
                        : [],
                days: [...days.entries()].sort(([a], [b]) => a - b).map(([, day]) => day),
            }
        })
        .filter((program): program is UITutorStudyProgram => program != null)
}

/** Le programme ouvert d'office : celui du chapitre déjà retenu, sinon le premier (`StudyProgramChapterSelector`). */
export function initialProgramId(
    programs: UITutorStudyProgram[],
    chapterId: string | number | null | undefined
): UITutorStudyProgram["id"] | undefined {
    const withChapter =
        chapterId != null
            ? programs.find((program) =>
                  program.days.some((day) => day.chapters.some((chapter) => String(chapter.id) === String(chapterId)))
              )
            : undefined
    return (withChapter ?? programs[0])?.id
}
